import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { YouTubeEmbed } from "../components/YouTubeEmbed";
import { MapleLeaf, MapleWatermark } from "../components/MapleLeaf";
import { IMAGES, MCC_PHOTOS } from "../data/images";
import { CAMPUS_TOUR_VIDEO } from "../data/successStories";
import { EASE } from "../utils/motion";
const SPACES = [{
  name: "Classrooms",
  copy: "Modern, well-equipped rooms built for discussion, collaboration and focused teaching.",
  image: IMAGES.classroom
}, {
  name: "Laboratories",
  copy: "Science labs where Biology, Chemistry and Physics move from the page to the bench.",
  image: IMAGES.lab
}, {
  name: "ICT",
  copy: "Technology spaces supporting Computer Science and everyday digital learning.",
  image: IMAGES.ict
}, {
  name: "Library & study areas",
  copy: "Quiet places to read, revise and work independently, available to every enrolled student.",
  image: MCC_PHOTOS.library
}, {
  name: "Student lounge",
  copy: "Chess, conversation and a wall signed by the Class of 2021, The Inexorables.",
  image: MCC_PHOTOS.inexorablesLounge
}, {
  name: "The rooftop",
  copy: "Panoramic views of the Lekki Peninsula: a place to pause between classes.",
  image: IMAGES.rooftop
}, {
  name: "Residences",
  copy: "Separate girls’ and boys’ residences, designed as a home away from home.",
  image: IMAGES.boarding
}];
function Space({
  index,
  onActive,
  children
}: {
  index: number;
  onActive: (i: number) => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    margin: "-45% 0px -45% 0px"
  });
  useEffect(() => {
    if (inView) onActive(index);
  }, [inView, index, onActive]);
  return <div ref={ref} className="flex min-h-[70vh] flex-col justify-center py-12" data-id="element-769" data-mp="33wujli08uoba">{children}</div>;
}
export function Campus() {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  return <>
      <PageHero variant="full" eyebrow="OUR CAMPUS" title="Step inside Maple." description="21st-century learning facilities in Lekki, one of Nigeria’s most dynamic neighbourhoods." image={IMAGES.rooftop} imageAlt="MCC rooftop terrace overlooking Lekki" breadcrumb="Our Campus" data-id="element-770" data-mp="3dpjuobrvu063" />

      <main data-id="element-771" data-mp="2pyee1ue4b3uq">
        <section className="relative overflow-hidden bg-[#061f38] py-24 text-white md:py-32" data-id="element-772" data-mp="2pzz1l020lmj3">
          <MapleWatermark className="-left-24 -top-20 h-[34rem] w-[34rem]" tone="text-white/[0.05]" rotate={-10} data-id="element-773" data-mp="2u1kkp3556x1q" />
          <div className="relative mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-774" data-mp="2yw6nwj12ljaq">
            <div className="grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-775" data-mp="98z8b2wi6ol4">
              <div className="lg:col-span-7" data-id="element-776" data-mp="e7aixfumh34e">
                <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-777" data-mp="ea53d7dmynxt"><MapleLeaf size={11} data-id="element-778" data-mp="oe3d8cenicgi" /> THE CAMPUS TOUR</p>
                <h2 className="mt-5 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-6xl" data-id="element-779" data-mp="jeh002ytow7p">Walk through Maple, from anywhere.</h2>
              </div>
              <p className="max-w-sm text-base leading-7 text-white/65 lg:col-span-4 lg:col-start-9" data-id="element-780" data-mp="al3w65z80a6u">A guided film of the Lekki campus: classrooms, labs, library and the spaces students call their own.</p>
            </div>
            <motion.div initial={{
            opacity: 0,
            clipPath: reduce ? "inset(0 0 0 0)" : "inset(10% 10% 10% 10%)"
          }} whileInView={{
            opacity: 1,
            clipPath: "inset(0% 0% 0% 0%)"
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: reduce ? 0.01 : 0.9,
            ease: EASE
          }} className="mt-14" data-id="element-781" data-mp="menn3t7s239">
              <YouTubeEmbed youtubeId={CAMPUS_TOUR_VIDEO.youtubeId} title={CAMPUS_TOUR_VIDEO.title} label="WATCH THE CAMPUS TOUR" data-id="element-782" data-mp="1m0ahtmayg4sv" />
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-9 md:py-32" data-id="element-783" data-mp="1y2ack05o05zt">
          <WordReveal as="p" text="Or take it one space at a time. Scroll to walk through." className="max-w-3xl text-3xl font-medium leading-[1.1] tracking-[-0.045em] md:text-5xl" data-id="element-784" data-mp="1y07npjg90iwo" />
        </section>

        <section className="mx-auto grid max-w-[1600px] gap-10 px-5 pb-24 md:px-9 lg:grid-cols-12" data-id="element-785" data-mp="1o67a0t6wi5cc">
          <div className="hidden lg:col-span-7 lg:block" data-id="element-786" data-mp="1aawkzwfc8efw">
            <div className="sticky top-24 h-[calc(100vh-8rem)] overflow-hidden bg-[#061f38]" data-id="element-787" data-mp="1f47vq8n9tfd3">
              <AnimatePresence initial={false} data-id="element-788" data-mp="10fl4p9oa6z61">
                <motion.img key={active} src={SPACES[active].image} alt={SPACES[active].name} initial={{
                opacity: 0,
                scale: reduce ? 1 : 1.08,
                clipPath: reduce ? "inset(0 0 0 0)" : "inset(0 0 100% 0)"
              }} animate={{
                opacity: 1,
                scale: 1,
                clipPath: "inset(0 0 0% 0)"
              }} exit={{
                opacity: 0
              }} transition={{
                duration: reduce ? 0.01 : 0.9,
                ease: EASE
              }} className="absolute inset-0 h-full w-full object-cover" data-id="element-789" data-mp="10cp75mgcici4" />
              </AnimatePresence>
              <div className="absolute bottom-0 left-0 flex items-center gap-4 bg-[#fbfaf6] px-5 py-4" data-id="element-790" data-mp="lpi6c9kkquwy">
                <span className="text-sm font-semibold tabular-nums text-[#bb1e35]" data-id="element-791" data-mp="vq0x7oak3bod">0{active + 1}</span>
                <span className="text-sm font-medium" data-id="element-792" data-mp="qqxpfw36yrcp">{SPACES[active].name}</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 lg:col-start-9" data-id="element-793" data-mp="15esixep0wzqu">
            {SPACES.map((s, i) => <Space key={s.name} index={i} onActive={setActive} data-id="element-794" data-mp="10ghcldxxt7dd" data-mpi={i}>
                <img src={s.image} alt={s.name} loading="lazy" className="mb-8 aspect-[4/3] w-full object-cover lg:hidden" data-id="element-795" data-mp="1egicaq2gu8r9" />
                <Reveal data-id="element-796" data-mp="19i70wawivq6x">
                  <span className={`text-sm font-semibold tabular-nums transition-colors duration-300 ${i === active ? "text-[#bb1e35]" : "text-[#9aa1a6]"}`} data-id="element-797" data-mp="19iqly0hgyup6">0{i + 1} / 0{SPACES.length}</span>
                  <h2 className={`mt-4 text-4xl font-medium leading-[0.95] tracking-[-0.06em] transition-colors duration-300 md:text-5xl ${i === active ? "text-[#061f38]" : "text-[#9aa1a6] lg:text-[#b6bbbe]"}`} data-id="element-798" data-mp="c3vtb84jxq2g">{s.name}</h2>
                  <p className="mt-5 max-w-sm text-base leading-7 text-[#4f5963]" data-id="element-799" data-mp="kwpd9iuy1dmn">{s.copy}</p>
                </Reveal>
              </Space>)}
          </div>
        </section>

        <section className="bg-[#061f38] py-24 text-white md:py-32" data-id="element-800" data-mp="us0ty6pwwux1">
          <div className="mx-auto grid max-w-[1440px] gap-10 px-5 md:px-9 lg:grid-cols-12" data-id="element-801" data-mp="zsf3jt75wcx6">
            <Reveal className="lg:col-span-6" data-id="element-802" data-mp="13us3u2353imk">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-803" data-mp="13th0u4hiqewi">FIND US</p>
              <h2 className="mt-5 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-6xl" data-id="element-804" data-mp="31lurujfs1foz">Block 127, Plot 9, Bunmi Olowude Street.</h2>
              <p className="mt-5 text-base text-white/60" data-id="element-805" data-mp="2y2no1ag4mukh">Lekki Phase 1, Lagos, near the 2nd Roundabout, by Pinnacle Petrol Station.</p>
            </Reveal>
            <Reveal delay={0.15} className="flex flex-wrap items-end gap-3 lg:col-span-5 lg:col-start-8 lg:justify-end" data-id="element-806" data-mp="2ta2so4hko9eu">
              <Link to="/gallery" className="group inline-flex items-center gap-2 border border-white/40 px-5 py-4 text-sm font-semibold transition-colors duration-200 hover:border-white" data-id="element-807" data-mp="2kapljigx7wxa">View the gallery <ArrowRightIcon size={16} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-808" data-mp="3gym5mxbkhad9" /></Link>
              <Link to="/contact" className="bg-[#c5233d] px-5 py-4 text-sm font-semibold transition-colors duration-200 hover:bg-[#a71931]" data-id="element-809" data-mp="3801t6dwlnwxh">Book a visit</Link>
            </Reveal>
          </div>
        </section>
      </main>

      <CTABand title="The best way to see Maple is in person." image={IMAGES.campus} data-id="element-810" data-mp="1wm6c2zm4xe3m" />
    </>;
}